import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import utils from "../../../universal/utils";
import adminFetch from "../../../universal/adminFetch";
import useIsMountedRef from "../../../hooks/useIsMountedRef";

const MODE_OPTIONS = [
  {
    value: 'legacy_fifo',
    label: 'Legacy FIFO',
    summary: 'Configurable FIFO testing mode for equal-team matches such as 1v1, 2v2, and 3v3.',
  },
  {
    value: 'skill_strict',
    label: 'Skill-Based',
    summary: 'Production-grade skill-based 5v5 with the full compatibility rules enforced.',
  },
  {
    value: 'skill_beta',
    label: 'Skill-Based + Beta',
    summary: 'Production-grade skill-based 5v5 with only map and server region overlap enforced.',
  }
];

const DEFAULT_SMALLER_MATCH_WAIT_SECONDS = 60;
const DEFAULT_SMALLER_MATCH_MOVEMENT_THRESHOLD = 1;

const LEGACY_FIFO_TEAM_SIZE_OPTIONS = [1, 2, 3, 4, 5].map((teamSize) => ({
  value: teamSize,
  label: `${teamSize}v${teamSize}`,
  playersPerMatch: teamSize * 2,
}));

function getModeMeta(mode) {
  return MODE_OPTIONS.find((option) => option.value === mode) || MODE_OPTIONS[0];
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  status: {
    marginTop: theme.spacing(1),
  }
}));

export default function MatchmakingAlgorithmSettings({ userId }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState('');
  const [matchmakingMode, setMatchmakingMode] = useState('legacy_fifo');
  const [legacyFifoTeamSize, setLegacyFifoTeamSize] = useState(1);
  const [smallerMatchFallback, setSmallerMatchFallback] = useState({
    enabled: false,
    waitSeconds: DEFAULT_SMALLER_MATCH_WAIT_SECONDS,
    movementThreshold: DEFAULT_SMALLER_MATCH_MOVEMENT_THRESHOLD,
  });
  const [updatedAtMs, setUpdatedAtMs] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/matchmaking-mode/adpass/${userId}`);
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to load matchmaking mode');
      if (!isMountedRef.current) return;
      setMatchmakingMode(typeof data.matchmakingMode === 'string' ? data.matchmakingMode : 'legacy_fifo');
      setLegacyFifoTeamSize(Number.isInteger(data.legacyFifoTeamSize) ? data.legacyFifoTeamSize : 1);
      setSmallerMatchFallback({
        enabled: data.smallerMatchFallback?.enabled === true,
        waitSeconds: Number.isInteger(data.smallerMatchFallback?.waitSeconds)
          ? data.smallerMatchFallback.waitSeconds
          : DEFAULT_SMALLER_MATCH_WAIT_SECONDS,
        movementThreshold: Number.isInteger(data.smallerMatchFallback?.movementThreshold)
          ? data.smallerMatchFallback.movementThreshold
          : DEFAULT_SMALLER_MATCH_MOVEMENT_THRESHOLD,
      });
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : null);
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [basePath, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(async ({
    nextMode,
    nextLegacyFifoTeamSize = legacyFifoTeamSize,
    nextSmallerMatchFallback = smallerMatchFallback,
    savingKey = nextMode,
  }) => {
    if (!userId) return;
    setSaving(true);
    setSavingMode(savingKey || 'legacy_fifo_size');
    setError('');
    setSuccess('');
    try {
      const res = await adminFetch(`${basePath}/api/v7/admin/settings/matchmaking-mode/adpass/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchmakingMode: nextMode,
          legacyFifoTeamSize: nextLegacyFifoTeamSize,
          smallerMatchFallback: nextSmallerMatchFallback,
        })
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.err || 'Failed to save matchmaking mode');
      if (!isMountedRef.current) return;
      const savedMode = typeof data.matchmakingMode === 'string' ? data.matchmakingMode : nextMode;
      const savedMeta = getModeMeta(savedMode);
      const savedLegacyFifoTeamSize = Number.isInteger(data.legacyFifoTeamSize)
        ? data.legacyFifoTeamSize
        : nextLegacyFifoTeamSize;
      const legacySizeChanged = savedLegacyFifoTeamSize !== legacyFifoTeamSize;
      setMatchmakingMode(savedMode);
      setLegacyFifoTeamSize(savedLegacyFifoTeamSize);
      setSmallerMatchFallback({
        enabled: data.smallerMatchFallback?.enabled === true,
        waitSeconds: Number.isInteger(data.smallerMatchFallback?.waitSeconds)
          ? data.smallerMatchFallback.waitSeconds
          : nextSmallerMatchFallback.waitSeconds,
        movementThreshold: Number.isInteger(data.smallerMatchFallback?.movementThreshold)
          ? data.smallerMatchFallback.movementThreshold
          : nextSmallerMatchFallback.movementThreshold,
      });
      setUpdatedAtMs(typeof data.updatedAtMs === 'number' ? data.updatedAtMs : Date.now());
      if (savedMode === 'legacy_fifo') {
        setSuccess(`${savedMeta.label} set to ${savedLegacyFifoTeamSize}v${savedLegacyFifoTeamSize} (${savedLegacyFifoTeamSize * 2} players per match).`);
      } else if (legacySizeChanged) {
        setSuccess(`Legacy FIFO saved as ${savedLegacyFifoTeamSize}v${savedLegacyFifoTeamSize} (${savedLegacyFifoTeamSize * 2} players per match). It will apply when Legacy FIFO mode is enabled.`);
      } else {
        setSuccess(`${savedMeta.label} mode enabled.`);
      }
    } catch (e) {
      if (isMountedRef.current) setError(e?.message || String(e));
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
        setSavingMode('');
      }
    }
  }, [basePath, isMountedRef, legacyFifoTeamSize, smallerMatchFallback, userId]);

  const activeMode = getModeMeta(matchmakingMode);
  const activeLegacyFifoMeta = LEGACY_FIFO_TEAM_SIZE_OPTIONS.find((option) => option.value === legacyFifoTeamSize)
    || LEGACY_FIFO_TEAM_SIZE_OPTIONS[0];
  const legacyModeSelected = matchmakingMode === 'legacy_fifo';
  const skillModeSelected = matchmakingMode === 'skill_strict' || matchmakingMode === 'skill_beta';
  const normalizedSmallerWaitSeconds = Number.parseInt(smallerMatchFallback.waitSeconds, 10);
  const normalizedSmallerMovementThreshold = Number.parseInt(smallerMatchFallback.movementThreshold, 10);

  return (
    <Paper className={classes.paper}>
      <Box className={classes.headerRow}>
        <Box>
          <Typography variant="h6">Matchmaking Algorithm</Typography>
          <Typography variant="body2" color="textSecondary">
            Choose the live matchmaking mode without restarting edge: legacy FIFO with configurable team sizes, or one of the two skill-based modes that try 5v5 first.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpen((v) => !v)}
          startIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box className={classes.row}>
          <Typography variant="body1" style={{ fontWeight: 600 }}>
            Mode: {activeMode.label}
          </Typography>

          <Button
            variant="outlined"
            onClick={load}
            disabled={loading || saving || !userId}
          >
            Refresh
          </Button>
        </Box>

        {updatedAtMs ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Last updated: {new Date(updatedAtMs).toISOString()}
          </Typography>
        ) : null}

        {loading ? (
          <Typography className={classes.status} variant="body2" color="textSecondary">
            Loading...
          </Typography>
        ) : null}

        {error ? (
          <Typography className={classes.status} variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {success ? (
          <Typography className={classes.status} variant="body2" style={{ color: '#1b5e20' }}>
            {success}
          </Typography>
        ) : null}

        <Box style={{ marginTop: 24, padding: 16, border: activeMode.value === 'skill_beta' ? '1px solid #ff9800' : '1px solid rgba(255,255,255,0.12)', borderRadius: 4 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 600, color: activeMode.value === 'skill_beta' ? '#ff9800' : undefined }}>
            Available Modes
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Legacy FIFO is the lightweight testing path and can now be set to 1v1, 2v2, 3v3, 4v4, or 5v5.
            Skill-Based and Skill-Based + Beta always try 5v5 first; smaller fallback is controlled separately below.
          </Typography>

          <Box className={classes.row}>
            {MODE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant="contained"
                style={option.value === matchmakingMode
                  ? { backgroundColor: '#ff9800', color: '#fff' }
                  : undefined}
                color={option.value === matchmakingMode ? 'default' : 'primary'}
                onClick={() => save({ nextMode: option.value })}
                disabled={loading || saving || !userId || option.value === matchmakingMode}
                size="small"
              >
                {saving && option.value === savingMode ? <CircularProgress size={18} /> : option.label}
              </Button>
            ))}
          </Box>

          {legacyModeSelected ? (
            <>
              <Box className={classes.row}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Legacy FIFO team size:
                </Typography>
                {LEGACY_FIFO_TEAM_SIZE_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    variant="contained"
                    style={option.value === legacyFifoTeamSize
                      ? { backgroundColor: '#ff9800', color: '#fff' }
                      : undefined}
                    color={option.value === legacyFifoTeamSize ? 'default' : 'primary'}
                    onClick={() => save({
                      nextMode: matchmakingMode,
                      nextLegacyFifoTeamSize: option.value,
                      savingKey: `legacy_fifo_size_${option.value}`,
                    })}
                    disabled={loading || saving || !userId || option.value === legacyFifoTeamSize}
                    size="small"
                  >
                    {saving && savingMode === `legacy_fifo_size_${option.value}`
                      ? <CircularProgress size={18} />
                      : option.label}
                  </Button>
                ))}
              </Box>

              <Typography className={classes.status} variant="body2" color="textSecondary">
                Current legacy FIFO setting: {activeLegacyFifoMeta.label} ({activeLegacyFifoMeta.playersPerMatch} players required before a match is created).
              </Typography>
            </>
          ) : (
            <Typography className={classes.status} variant="body2" color="textSecondary">
              Current primary requirement: 10 players for 5v5. Optional smaller fallback is controlled below.
            </Typography>
          )}

          <Typography className={classes.status} variant="body2" color="textSecondary">
            {activeMode.summary}
          </Typography>
        </Box>

        {skillModeSelected ? (
          <Box style={{ marginTop: 24, padding: 16, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 4 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Smaller-Match Skill Fallback
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
              When enabled, players can opt into 2v2, 3v3, or 4v4 once they have waited this long and the queue population has stopped moving for the same period. 5v5 remains the first path on every tick.
            </Typography>

            <Box className={classes.row}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smallerMatchFallback.enabled === true}
                    onChange={(event) => {
                      const next = {
                        ...smallerMatchFallback,
                        enabled: event.target.checked,
                      };
                      setSmallerMatchFallback(next);
                      save({
                        nextMode: matchmakingMode,
                        nextSmallerMatchFallback: next,
                        savingKey: 'smaller_match_fallback',
                      });
                    }}
                    color="primary"
                    disabled={loading || saving || !userId}
                  />
                }
                label="Enable smaller-match fallback"
              />
              <TextField
                label="Wait seconds"
                type="number"
                value={smallerMatchFallback.waitSeconds}
                onChange={(event) => {
                  const waitSeconds = event.target.value;
                  setSmallerMatchFallback((current) => ({
                    ...current,
                    waitSeconds,
                  }));
                }}
                onBlur={() => {
                  const next = {
                    ...smallerMatchFallback,
                    waitSeconds: Number.isInteger(normalizedSmallerWaitSeconds)
                      ? normalizedSmallerWaitSeconds
                      : DEFAULT_SMALLER_MATCH_WAIT_SECONDS,
                  };
                  setSmallerMatchFallback(next);
                  save({
                    nextMode: matchmakingMode,
                    nextSmallerMatchFallback: next,
                    savingKey: 'smaller_match_wait',
                  });
                }}
                inputProps={{ min: 60, max: 1800, step: 30 }}
                size="small"
                variant="outlined"
                disabled={loading || saving || !userId}
              />
              <TextField
                label="Movement threshold"
                type="number"
                value={smallerMatchFallback.movementThreshold}
                helperText="Queue-size change allowed before the stability window unlocks. After that, join/leave churn does not reset it."
                onChange={(event) => {
                  const movementThreshold = event.target.value;
                  setSmallerMatchFallback((current) => ({
                    ...current,
                    movementThreshold,
                  }));
                }}
                onBlur={() => {
                  const next = {
                    ...smallerMatchFallback,
                    movementThreshold: Number.isInteger(normalizedSmallerMovementThreshold)
                      ? normalizedSmallerMovementThreshold
                      : DEFAULT_SMALLER_MATCH_MOVEMENT_THRESHOLD,
                  };
                  setSmallerMatchFallback(next);
                  save({
                    nextMode: matchmakingMode,
                    nextSmallerMatchFallback: next,
                    savingKey: 'smaller_match_movement',
                  });
                }}
                inputProps={{ min: 0, max: 10, step: 1 }}
                size="small"
                variant="outlined"
                disabled={loading || saving || !userId}
              />
              {saving && ['smaller_match_fallback', 'smaller_match_wait', 'smaller_match_movement'].includes(savingMode) ? (
                <CircularProgress size={18} />
              ) : null}
            </Box>

            <Typography className={classes.status} variant="body2" color="textSecondary">
              Current fallback: {smallerMatchFallback.enabled ? 'enabled' : 'disabled'} after {smallerMatchFallback.waitSeconds || DEFAULT_SMALLER_MATCH_WAIT_SECONDS}s of waiting with queue movement threshold {smallerMatchFallback.movementThreshold ?? DEFAULT_SMALLER_MATCH_MOVEMENT_THRESHOLD}. No environment variable enables this.
            </Typography>
          </Box>
        ) : null}
      </Collapse>
    </Paper>
  );
}
